import React from 'react';
import { Select, Button, Slider } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  FullscreenOutlined,
  ReloadOutlined,
  SoundOutlined,
  AudioMutedOutlined
} from '@ant-design/icons';
import {
  PROTOCOLS,
  formatLastSeen,
  formatBatteryText,
  getBatteryTone
} from '../../utils/multiWindowLive';

/**
 * MultiWindowVideoPlayer 的纯展示子组件（自 MultiWindowVideoPlayer.js 原样迁出）。
 * 只负责渲染，状态与事件处理全部由父组件经 props 传入，DOM 结构/类名/文案不变。
 */

const { Option } = Select;

/** 全屏常显：队员名 + 当前时间 + 设备电量 */
export function FullscreenInfoBar({
  memberName,
  streamKey,
  fullscreenClock,
  batteryLevel,
  lastSeen,
}) {
  const batteryText = formatBatteryText(batteryLevel);
  const batteryTone = getBatteryTone(batteryLevel);

  return (
    <div className="fullscreen-info-bar">
      <span className="fullscreen-info-bar__name">{memberName || streamKey}</span>
      <span className="fullscreen-info-bar__time">{fullscreenClock || '--:--:--'}</span>
      <span className={`fullscreen-info-bar__battery fullscreen-info-bar__battery--${batteryTone}`}>
        电量 {batteryText}
      </span>
      {lastSeen && (
        <span className="fullscreen-info-bar__seen">
          上报 {formatLastSeen(lastSeen)}
        </span>
      )}
    </div>
  );
}

/** 顶部：队员名 / 推流码 / 协议选择 */
export function VideoHeader({
  memberName,
  streamKey,
  currentProtocol,
  switchProtocol,
}) {
  return (
    <div className="video-header">
      <div className="stream-info">
        <span className="member-name">{memberName}</span>
        <span className="stream-key">({streamKey})</span>
      </div>
      
      <div className="protocol-selector">
        <Select
          value={currentProtocol}
          onChange={switchProtocol}
          size="small"
          style={{ width: 80 }}
        >
          {Object.entries(PROTOCOLS).map(([key, config]) => (
            <Option key={key} value={key}>
              <span style={{ color: config.color }}>{config.name}</span>
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
}

/** 手机端：单击画面时在顶部半透明显示状态信息 */
export function VideoStatusOverlay({
  currentProtocol,
  isPlaying,
  currentDelay,
  videoDimensions,
  videoOrientation,
  autoOrient,
  rotationAngle,
  flipHorizontal,
  flipVertical,
  isAccelerating,
  isInitialized,
}) {
  return (
    <div className="video-status-overlay" onClick={(e) => e.stopPropagation()}>
      <span style={{ color: PROTOCOLS[currentProtocol]?.color }}>
        {PROTOCOLS[currentProtocol]?.name}
      </span>
      <span>
        延迟 {isPlaying ? `${currentDelay.toFixed(1)}s` : PROTOCOLS[currentProtocol]?.latency}
      </span>
      {videoDimensions.width > 0 && (
        <span>
          {videoOrientation === 'portrait' ? '竖屏' : '横屏'}{' '}
          {videoDimensions.width}×{videoDimensions.height}
          {autoOrient ? ' · 自动' : ' · 手动'}
        </span>
      )}
      {rotationAngle !== 0 && <span>旋{rotationAngle}°</span>}
      {flipHorizontal && <span>左右</span>}
      {flipVertical && <span>上下</span>}
      <span>
        {isPlaying ? (isAccelerating ? '加速中' : '播放中') : isInitialized ? '已停止' : '等待'}
      </span>
    </div>
  );
}

/** 单击画面出现：旋转 / 翻转（普通窗与全屏均可用） */
export function RotateHud({
  rotateLeft,
  rotateRight,
  flipHorizontal,
  flipVertical,
  toggleFlipHorizontal,
  toggleFlipVertical,
  rotationAngle,
  resetRotation,
  setShowRotateHud,
}) {
  return (
    <div
      className="rotate-hud"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="rotate-hud__row">
        <button
          type="button"
          className="rotate-hud__btn"
          onClick={rotateLeft}
          title="向左旋转90度"
          aria-label="向左旋转90度"
        >
          <span className="rotate-hud__icon">↶</span>
          <span className="rotate-hud__label">左转</span>
        </button>
        <button
          type="button"
          className="rotate-hud__btn"
          onClick={rotateRight}
          title="向右旋转90度"
          aria-label="向右旋转90度"
        >
          <span className="rotate-hud__icon">↷</span>
          <span className="rotate-hud__label">右转</span>
        </button>
      </div>
      <div className="rotate-hud__row">
        <button
          type="button"
          className={`rotate-hud__btn${flipHorizontal ? ' rotate-hud__btn--active' : ''}`}
          onClick={toggleFlipHorizontal}
          title="左右翻转（镜像）"
          aria-label="左右翻转"
        >
          <span className="rotate-hud__icon">⇄</span>
          <span className="rotate-hud__label">左右</span>
        </button>
        <button
          type="button"
          className={`rotate-hud__btn${flipVertical ? ' rotate-hud__btn--active' : ''}`}
          onClick={toggleFlipVertical}
          title="上下翻转"
          aria-label="上下翻转"
        >
          <span className="rotate-hud__icon">⇅</span>
          <span className="rotate-hud__label">上下</span>
        </button>
      </div>
      {(rotationAngle !== 0 || flipHorizontal || flipVertical) && (
        <button
          type="button"
          className="rotate-hud__btn rotate-hud__btn--reset"
          onClick={(e) => {
            e.stopPropagation();
            resetRotation();
            setShowRotateHud(true);
          }}
          title="重置旋转与翻转"
          aria-label="重置旋转与翻转"
        >
          <span className="rotate-hud__icon">↺</span>
          <span className="rotate-hud__label">复位</span>
        </button>
      )}
    </div>
  );
}

/** 错误提示 + 手动重试 / 重置 */
export function VideoErrorPanel({
  error,
  retryCount,
  isRetrying,
  reload,
  onHardReset,
}) {
  return (
    <div className="error-message">
      <div className="error-icon">❌</div>
      <div className="error-text">{error}</div>
      {retryCount > 0 && (
        <div className="retry-info">
          已重试 {retryCount} 次
          {isRetrying && <span style={{ color: '#faad14' }}> (重试中...)</span>}
        </div>
      )}
      <div className="error-actions">
        <Button 
          size="small" 
          onClick={reload} 
          icon={<ReloadOutlined />}
          disabled={isRetrying}
        >
          {isRetrying ? '重试中...' : '手动重试'}
        </Button>
        {retryCount >= 5 && (
          <Button 
            size="small" 
            type="primary" 
            onClick={onHardReset}
            style={{ marginLeft: '8px' }}
          >
            重置
          </Button>
        )}
      </div>
    </div>
  );
}

/** 播放控制覆盖层（仅桌面悬停显示） */
export function VideoControlsBar({
  isPlaying,
  togglePlay,
  autoOrient,
  toggleAutoOrient,
  rotateLeft,
  rotateRight,
  flipHorizontal,
  flipVertical,
  toggleFlipHorizontal,
  toggleFlipVertical,
  resetRotation,
  finalMutedState,
  toggleMute,
  volume,
  handleVolumeChange,
  toggleFullscreen,
  onStopClick,
}) {
  return (
    <div className="video-controls">
      <div className="controls-left">
        <div className="playback-controls">
          <Button
            type="text"
            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={togglePlay}
            className="control-btn"
            title={isPlaying ? '暂停' : '继续'}
          />
          <Button
            type="text"
            icon={<StopOutlined />}
            onClick={onStopClick}
            className="control-btn"
            title="停止"
          />
          <Button
            type="text"
            onClick={toggleAutoOrient}
            className="control-btn"
            title={autoOrient ? '自动方向：开' : '自动方向：关'}
            style={{
              fontSize: '11px',
              padding: '0 4px',
              color: autoOrient ? '#52c41a' : '#888'
            }}
          >
            {autoOrient ? '自动' : '手动'}
          </Button>
          <Button type="text" icon="↶" onClick={rotateLeft} className="control-btn" title="向左旋转90度" style={{ fontSize: '12px' }} />
          <Button type="text" icon="↷" onClick={rotateRight} className="control-btn" title="向右旋转90度" style={{ fontSize: '12px' }} />
          <Button
            type="text"
            onClick={toggleFlipHorizontal}
            className="control-btn"
            title="左右翻转"
            style={{ fontSize: '12px', color: flipHorizontal ? '#52c41a' : undefined }}
          >
            ⇄
          </Button>
          <Button
            type="text"
            onClick={toggleFlipVertical}
            className="control-btn"
            title="上下翻转"
            style={{ fontSize: '12px', color: flipVertical ? '#52c41a' : undefined }}
          >
            ⇅
          </Button>
          <Button type="text" icon="🔄" onClick={resetRotation} className="control-btn" title="重置旋转与翻转" style={{ fontSize: '12px' }} />
        </div>
      </div>
      <div className="controls-right">
        <div className="volume-control">
          <Button
            type="text"
            icon={finalMutedState ? <AudioMutedOutlined /> : <SoundOutlined />}
            onClick={toggleMute}
            className="control-btn"
            title={finalMutedState ? '取消静音' : '静音'}
          />
          <Slider
            min={0}
            max={1}
            step={0.1}
            value={volume}
            onChange={handleVolumeChange}
            style={{ width: 60, marginLeft: 8 }}
          />
        </div>
        <Button
          type="text"
          icon={<FullscreenOutlined />}
          onClick={toggleFullscreen}
          className="control-btn"
          title="全屏"
        />
      </div>
    </div>
  );
}

/** PC 底栏：协议 / 延迟 / 方向 / 播放状态 */
export function VideoFooterStatus({
  currentProtocol,
  isPlaying,
  currentDelay,
  isAccelerating,
  videoDimensions,
  videoOrientation,
  autoOrient,
  rotationAngle,
  flipHorizontal,
  flipVertical,
  isInitialized,
}) {
  return (
    <div className="video-footer">
      <div className="status-info">
        <span className="protocol-name" style={{ color: PROTOCOLS[currentProtocol]?.color }}>
          {PROTOCOLS[currentProtocol]?.name}
        </span>
        <span className="latency-info">
          延迟: {isPlaying ? `${currentDelay.toFixed(1)}s` : PROTOCOLS[currentProtocol]?.latency}
          {isAccelerating && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>🚀</span>}
        </span>
        {videoDimensions.width > 0 && (
          <span className="orientation-info" style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>
            {videoOrientation === 'portrait' ? '📱竖屏' : '🖥️横屏'}{' '}
            {videoDimensions.width}×{videoDimensions.height}
            {autoOrient ? ' · 自动' : ' · 手动'}
            {rotationAngle !== 0 && (
              <span style={{ color: '#ffd700', marginLeft: '4px' }}>
                旋转: {rotationAngle}°
              </span>
            )}
            {flipHorizontal && (
              <span style={{ color: '#69c0ff', marginLeft: '4px' }}>左右翻转</span>
            )}
            {flipVertical && (
              <span style={{ color: '#69c0ff', marginLeft: '4px' }}>上下翻转</span>
            )}
          </span>
        )}
      </div>

      <div className="stream-status">
        <span className={`status-dot ${isPlaying ? 'playing' : isInitialized ? 'stopped' : 'waiting'}`}></span>
        <span className="status-text">
          {isPlaying ? (isAccelerating ? '加速播放中' : '播放中') : isInitialized ? '已停止' : '等待播放'}
        </span>
      </div>
    </div>
  );
}
